import React, { useState, useEffect } from 'react';
import T from '../../../components/common/T';
import { useNavigate, useParams } from 'react-router-dom';
import { usePurchaseOrder } from '../../../context/PurchaseOrderContext';
import { useVendors } from '../../../context/VendorContext';
import ConfirmationModal from '../../../components/ConfirmationModal';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { PaperAirplaneIcon, PlusIcon, ArrowLeftIcon, EnvelopeIcon } from "@heroicons/react/24/outline";
import { useAuth } from '../../../context/AuthContext';
import Price from "../../../components/common/Price";
const QuotationManagement = () => {
  const { prId } = useParams(); 
  const navigate = useNavigate();
  
  // 🟢 Get Vendors from Context
  const { vendors } = useVendors(); 
  
  const { getPR, loadQuotations, getQuotationsForPR, addQuotation, selectQuotation, getSelectedQuotation, createPurchaseOrder } = usePurchaseOrder();
  const { checkAccess } = useAuth();

  // 🟢 Load data on mount
  useEffect(() => {
      if (prId) loadQuotations(prId);
  }, [prId]); 

  const pr = getPR(prId);
  const quotations = getQuotationsForPR(prId);
  const selectedQuote = getSelectedQuotation(prId);

  const [selectedVendorId, setSelectedVendorId] = useState("");
  const [manualPrice, setManualPrice] = useState("");
  const [modalConfig, setModalConfig] = useState({ isOpen: false, title: "", message: "", onConfirm: null, isDanger: false, confirmText: "Confirm" });

  const closeModal = () => setModalConfig({ ...modalConfig, isOpen: false });

  if (!pr) return <div className="p-6"><T>Loading PR...</T></div>;

  // --- ACTIONS ---

  // 1. ADD MANUAL QUOTE
  const handleAddManualQuote = async () => {
      if(!selectedVendorId || !manualPrice) return toast.warn("Enter Vendor and Price");
      
      const vendor = vendors.find(v => String(v.id) === String(selectedVendorId));
      const vendorName = vendor ? (vendor.name || vendor.vendorName) : "Unknown Vendor";

      await addQuotation(prId, {
          vendorId: selectedVendorId,
          vendorName: vendorName,
          price: Number(manualPrice),
          status: 'Pending'
      });
      
      setManualPrice("");
      setSelectedVendorId("");
      loadQuotations(prId); 
      toast.success("Manual Quote Added");
  };

const handleSendRFQ = async () => {
      if(!selectedVendorId) return toast.warn("Select a Vendor to send RFQ");
      
      const vendor = vendors.find(v => String(v.id) === String(selectedVendorId));
      
      // 🟢 FIX: Fallback name prevents "Sent to undefined"
      const vendorName = vendor ? (vendor.name || vendor.vendorName) : "Selected Vendor";

      await addQuotation(prId, {
          vendorId: selectedVendorId,
          vendorName: vendorName,
          price: 0, 
          status: 'Sent' // 🟢 This MUST be 'Sent' for the portal to see it
      });

      setSelectedVendorId("");
      loadQuotations(prId); 
      toast.success(`RFQ Sent to ${vendorName}`);
  };

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
        
        {/* HEADER */}
        <div className="flex items-center gap-4 mb-6">
            <button onClick={() => navigate('/admin/material/purchase')} className="bg-white border p-2 rounded-full text-gray-500 hover:text-gray-800 transition">
                <ArrowLeftIcon className="w-5 h-5" />
            </button>
            <div>
                <h1 className="text-base font-bold text-gray-800"><T>Compare Quotations</T></h1>
                <p className="text-sm text-gray-500"><T>For PR: </T><span className="font-bold text-purple-700">{pr.prNumber}</span> • {pr.materialName}</p>
            </div>
        </div>

        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-200">
            
            {/* ACTION BAR */}
            <div className="flex flex-col md:flex-row gap-4 items-end mb-8 border-b pb-6 bg-gray-50 p-4 rounded-lg">
                <div className="flex-1 w-full">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Select Vendor</T></label>
                    <select 
                        className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-purple-500 bg-white"
                        value={selectedVendorId}
                        onChange={e => setSelectedVendorId(e.target.value)}
                    >
                        <option value=""><T>Choose Vendor...</T></option>
                        {vendors.map(v => <option key={v.id} value={v.id}>{v.name || v.vendorName}</option>)}
                    </select>
                </div>
                
                <div className="w-full md:w-32">
                    <label className="block text-xs font-bold text-gray-500 uppercase mb-1"><T>Price (₹)</T></label>
                    <input 
                        type="number" 
                        className="w-full border border-gray-300 rounded-lg p-2.5 outline-none focus:ring-2 focus:ring-purple-500"
                        placeholder="0.00"
                        value={manualPrice}
                        onChange={e => setManualPrice(e.target.value)}
                    />
                </div>

                <div className="flex gap-2">
                    <button 
                        onClick={handleAddManualQuote}
                        className="bg-white border border-gray-300 text-gray-700 px-4 py-2.5 rounded-lg font-bold hover:bg-gray-100 transition flex items-center gap-2 text-sm"
                    >
                        <PlusIcon className="w-5 h-5" /> Add Manual
                    </button>

                    <button 
                        onClick={handleSendRFQ}
                        className="bg-purple-700 text-white px-4 py-2.5 rounded-lg font-bold shadow hover:bg-purple-800 transition flex items-center gap-2 text-sm"
                    >
                        <EnvelopeIcon className="w-5 h-5" /> Send RFQ
                    </button>
                </div>
            </div>

            {/* QUOTES LIST */}
            <h3 className="font-bold text-gray-800 mb-4"><T>Quotations</T></h3>
            {quotations.length === 0 ? (
                <p className="text-gray-400 italic mb-6"><T>No quotations yet.</T></p>
            ) : (
                <div className="space-y-3">
                    {quotations.map(q => (
                        <div key={q.id} className={`flex justify-between items-center p-4 border rounded-xl transition ${q.isSelected ? 'border-green-500 bg-green-50' : 'border-gray-200'}`}>
                            <div>
                                <div className="flex items-center gap-2">
                                    <h4 className="font-bold text-gray-800">{q.vendorName}</h4>
                                    {q.status === 'Sent' && <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-0.5 rounded-full"><T>Waiting for Vendor</T></span>}
                                    {q.status === 'Pending' && <span className="text-xs bg-blue-100 text-blue-700 px-2 py-0.5 rounded-full"><T>Received</T></span>}
                                </div>
                                <p className="text-sm text-gray-500"><T>Price: </T><span className="font-bold text-gray-900"><Price amount={Number(q.price)} /></span></p>
                            </div>
                            
                            {/* Allow selection if Price is set OR if we are just testing flow */}
                            <button 
                                onClick={() => selectQuotation(q.id)}
                                disabled={q.isSelected}
                                className={`px-4 py-2 rounded-lg font-bold text-xs uppercase tracking-wide transition ${
                                    q.isSelected 
                                    ? 'bg-green-600 text-white cursor-default' 
                                    : 'bg-white border border-gray-300 text-gray-700 hover:bg-gray-100'
                                }`}
                            >
                                {q.isSelected ? 'Selected' : 'Select'}
                            </button>
                        </div>
                    ))}
                </div>
            )}

            {/* CREATE PO ACTION */}
            <div className="mt-8 pt-6 border-t">
                <button 
                    onClick={() => {
                        if(!selectedQuote) return;
                        setModalConfig({
                            isOpen: true,
                            title: "Confirm Purchase Order",
                            message: `Generate PO for ${selectedQuote.vendorName}?`,
                            confirmText: "Create PO",
                            onConfirm: () => {
                                createPurchaseOrder(prId, selectedQuote.id);
                                closeModal();
                                // Navigate AFTER PO is created
                                setTimeout(() => navigate('/admin/material/purchase'), 1000);
                            }
                        });
                    }}
                    disabled={!selectedQuote}
                    className={`w-full py-4 rounded-xl text-lg font-bold shadow-md transition flex justify-center items-center gap-2 ${
                        selectedQuote 
                        ? 'bg-gray-900 text-white hover:bg-black' 
                        : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                    }`}
                >
                    <PaperAirplaneIcon className="w-6 h-6" />
                    Generate Purchase Order
                </button>
            </div>

        </div>
        
        <ConfirmationModal 
            isOpen={modalConfig.isOpen} 
            title={modalConfig.title} 
            message={modalConfig.message} 
            onConfirm={modalConfig.onConfirm} 
            onCancel={closeModal} 
            confirmText={modalConfig.confirmText} 
        />
    </div>
  );
};

export default QuotationManagement;